'use client';
import { useEffect, useRef, type ReactElement } from 'react';
import BevSafeText from '../../../bev-safe-text';
import Button from '../../../button';
import Image from '../../../image';
import CheckmarkFill from '../../../icon/checkmark_fill';
import CloseAlt from '../../../icon/close-alt';

import getAddedToCartSummary from './get-added-to-cart-summary';
import type { EventProduct } from './types';

type Translate = (text: string, params?: (string | number)[]) => string;

export type AddedToCartPopupProps = {
	item: EventProduct;
	locale: string;
	t: Translate;
	onClose: () => void;
	onViewCart: () => void;
};

/**
 * "Added to Cart" confirmation shown after an ancillary lands in the cart (BUA1-29, Figma
 * 30287:47685 desktop / 30287:47709 mobile): a card pinned top-right under the nav from `md:` up,
 * a bottom sheet over a backdrop below it.
 */
export default function AddedToCartPopup({
	item,
	locale,
	t,
	onClose,
	onViewCart,
}: AddedToCartPopupProps): ReactElement {
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const { name, image, dateTime, details, price } = getAddedToCartSummary(item, t, locale);

	useEffect(() => {
		closeButtonRef.current?.focus();
		const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [onClose]);

	return (
		// ponytail: md:top-[96px] is the Figma desktop nav height; anchor to the cart icon if the nav changes
		<div
			className="fixed inset-0 z-1000 flex items-end md:inset-auto md:top-[96px] md:right-0"
			data-testid="added-to-cart-popup"
		>
			<div className="absolute inset-0 bg-backdrop md:hidden" onClick={onClose} aria-hidden />
			<div
				role="dialog"
				aria-labelledby="added-to-cart-popup-title"
				className="relative flex w-full flex-col gap-200 rounded-t-[20px] bg-surface-default px-200 pt-250 pb-400 shadow-raised md:w-[430px] md:rounded-none md:p-300"
			>
				<div className="flex flex-col gap-060 md:gap-100">
					<button
						ref={closeButtonRef}
						type="button"
						onClick={onClose}
						className="self-end cursor-pointer"
						aria-label={t('Close')}
					>
						<CloseAlt />
					</button>
					<div className="flex items-center gap-100">
						<span aria-hidden className="flex">
							<CheckmarkFill width={20} height={20} className="fill-success-icon" />
						</span>
						<h2 id="added-to-cart-popup-title" className="text-subtitle1 text-text-heading">
							{t('Added to Cart')}
						</h2>
					</div>
				</div>

				<div className="flex items-start gap-150">
					{image && (
						<Image
							src={image}
							alt={name || ''}
							roughSize="s"
							className="size-[96px] shrink-0 object-cover md:size-[135px]"
						/>
					)}
					<div className="flex flex-col gap-040 md:gap-060">
						<p className="text-subtitle2 text-text-heading">
							<BevSafeText>{name}</BevSafeText>
						</p>
						<p className="text-body3 text-text-primary">
							{dateTime}
							{dateTime && details && <br />}
							{details}
						</p>
						{price && <p className="text-subtitle2 text-text-heading">{price}</p>}
					</div>
				</div>

				<p className="text-caption text-text-primary">{t('Taxes and fees may apply.')}</p>

				<div className="flex flex-col gap-150">
					<Button intent="filled" className="w-full" onClick={onClose}>
						{t('Continue Shopping')}
					</Button>
					<Button intent="outline" className="w-full" onClick={onViewCart}>
						{t('View Cart')}
					</Button>
				</div>
			</div>
		</div>
	);
}
